import React, { createContext, useContext, useEffect, useState } from "react";
import keycloak from "../services/keycloak";

type KeycloakInstanceType = typeof keycloak;

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  login: () => void;
  logout: () => void;
  keycloakInstance: KeycloakInstanceType | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!sessionStorage.getItem("auth_token"));
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem("auth_token"));
  const [isLoading, setIsLoading] = useState(() => !sessionStorage.getItem("just_logged_out"));
  const [keycloakInstance, setKeycloakInstance] = useState<KeycloakInstanceType | null>(null);
  
  const MIN_VALIDITY = Number(process.env.MIN_VALIDITY_TOKEN) || 70;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.crypto) return;

    const initKeycloak = async () => {
      try {
        const justLoggedOut = sessionStorage.getItem("just_logged_out");
        
        if (justLoggedOut) {
          sessionStorage.removeItem("just_logged_out");
          await keycloak.init({
            onLoad: "check-sso",
            silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
            pkceMethod: "S256",
            checkLoginIframe: false,
          });
          setIsAuthenticated(false);
          setToken(null);
        } else {
          const storedToken = sessionStorage.getItem("auth_token");
          const storedRefreshToken = sessionStorage.getItem("refresh_token");

          const authenticated = await keycloak.init({
            onLoad: "check-sso",
            silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
            pkceMethod: "S256",
            checkLoginIframe: false,
            token: storedToken || undefined,
            refreshToken: storedRefreshToken || undefined,
          });

          if (authenticated && keycloak.token && keycloak.refreshToken) {
            sessionStorage.setItem("auth_token", keycloak.token);
            sessionStorage.setItem("refresh_token", keycloak.refreshToken);
            if (keycloak.idToken) sessionStorage.setItem("id_token", keycloak.idToken);
            setIsAuthenticated(true);
            setToken(keycloak.token);
          } else if (storedToken && storedRefreshToken) {
            try {
              await keycloak.updateToken(-1);
              if (keycloak.token && keycloak.refreshToken) {
                sessionStorage.setItem("auth_token", keycloak.token);
                sessionStorage.setItem("refresh_token", keycloak.refreshToken);
                if (keycloak.idToken) sessionStorage.setItem("id_token", keycloak.idToken);
                setIsAuthenticated(true);
                setToken(keycloak.token);
              } else {
                throw new Error("Refresh failed");
              }
            } catch (refreshError) {
              sessionStorage.removeItem("auth_token");
              sessionStorage.removeItem("refresh_token");
              sessionStorage.removeItem("id_token");
              setIsAuthenticated(false);
              setToken(null);
            }
          }
        }
        
        setKeycloakInstance(keycloak);
      } catch (error) {
        console.error("Failed to initialize Keycloak:", error);
        sessionStorage.removeItem("auth_token");
        sessionStorage.removeItem("refresh_token");
        sessionStorage.removeItem("id_token");
        setIsAuthenticated(false);
        setToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    initKeycloak();
  }, []);

  const login = () => {
    if (typeof window === 'undefined' || !window.crypto) {
      console.error("Login impossible: Web Crypto API indisponible");
      return;
    }
    keycloak.login({});
  };

  const logout = () => {
    if (!keycloak) {
      console.error("Keycloak non initialisé");
      return;
    }
    
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("id_token");
    sessionStorage.setItem("just_logged_out", "true");
    
    setIsAuthenticated(false);
    setToken(null);
    
    keycloak.logout();
  };

  useEffect(() => {
    if (!keycloakInstance?.authenticated) return;

    const refreshInterval = setInterval(() => {
      keycloakInstance.updateToken(MIN_VALIDITY)
        .then((refreshed: boolean) => {
          if (refreshed && keycloakInstance.token && keycloakInstance.refreshToken) {
            sessionStorage.setItem("auth_token", keycloakInstance.token);
            sessionStorage.setItem("refresh_token", keycloakInstance.refreshToken);
            setToken(keycloakInstance.token);
          }
        })
        .catch(() => {
          console.error('Token refresh failed');
          sessionStorage.removeItem("auth_token");
          sessionStorage.removeItem("refresh_token");
          sessionStorage.removeItem("id_token");
          setIsAuthenticated(false);
          setToken(null);
          clearInterval(refreshInterval);
          keycloak.logout();
        });
    }, 5000);

    return () => clearInterval(refreshInterval);
  }, [keycloakInstance, MIN_VALIDITY]);
  const contextValue: AuthContextType = {
    isAuthenticated,
    token,
    login,
    logout,
    keycloakInstance,
    isLoading,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};